import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import { requestLeagueIntel } from '../utils/leagueIntelRequest';
import './LeagueIntelDesk.css';

const prompts = {
  dynasty: [
    'What is my biggest roster weakness, and what type of move should I make?',
    'Which public roster fits make sense for a trade conversation?',
    'Should my team contend, retool or rebuild based on the current evidence?',
    'Explain my playoff path and remaining schedule in plain language.',
  ],
  lor: [
    'What is my highest-impact move for this week?',
    'Where is my lineup most vulnerable based on current projections?',
    'Which public roster fits make sense for a trade conversation?',
    'Explain my playoff path and remaining schedule in plain language.',
  ],
};

const displayTime = value => {
  if (!value) return 'time unavailable';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const safeUrl = value => {
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
  } catch { return ''; }
};

const deskBotStatus = {
  idle: ['LEAGUE NODE ONLINE', 'Ready for a team question'],
  analyzing: ['READING THE LEAGUE', 'Checking public rosters and current evidence'],
  complete: ['ANSWER READY', 'League-visible evidence reconciled'],
  error: ['CONNECTION INTERRUPTED', 'Try the question again'],
};

function LeagueDeskBot({ state = 'idle' }) {
  const [label, detail] = deskBotStatus[state] || deskBotStatus.idle;
  return <div className={`lid-bot lid-bot--${state}`}>
    <div className="lid-bot-avatar" role="img" aria-label="LeagueIntel league advisor bot">
      <span className="lid-bot-ring" />
      <svg viewBox="0 0 80 80" aria-hidden="true">
        <defs><linearGradient id="leagueBotShell" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#bffcff"/><stop offset="1" stopColor="#219ba6"/></linearGradient></defs>
        <path className="lid-bot-shell" d="M40 8 66 23v34L40 72 14 57V23z"/>
        <path className="lid-bot-face" d="M40 17 58 28v24L40 63 22 52V28z"/>
        <path className="lid-bot-signal" d="M29 35h22M31 52h18"/>
        <rect className="lid-bot-eye" x="28" y="39" width="8" height="6" rx="3"/>
        <rect className="lid-bot-eye" x="44" y="39" width="8" height="6" rx="3"/>
        <circle className="lid-bot-core" cx="40" cy="53" r="3"/>
      </svg>
      <span className="lid-bot-scan" />
    </div>
    <div><small>LEAGUEINTEL // SHARED DESK</small><strong>{label}</strong><span>{detail}</span></div>
    <i aria-hidden="true" />
  </div>;
}

export default function LeagueIntelDesk({ api, league = 'dynasty' }) {
  const { currentUser } = useUser();
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const label = league === 'lor' ? 'League of Record' : 'Dynasty';
  const botState = busy ? 'analyzing' : error ? 'error' : result ? 'complete' : 'idle';

  const ask = async event => {
    event.preventDefault();
    const prompt = question.trim();
    if (!prompt) return;
    setBusy(true); setError('');
    try {
      const payload = await requestLeagueIntel({
        api, path: '/api/league-agent/chat',
        body: { question: prompt, league, history: history.slice(-8) },
      });
      setResult(payload);
      setHistory(previous => [...previous, { role: 'user', content: prompt }, { role: 'assistant', content: payload.answer }].slice(-8));
      setQuestion('');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (currentUser?.guest || !currentUser) return <div className="empty-state"><p>Sign in as an active league manager to use LeagueIntel Desk.</p></div>;

  return <div className="lid-page">
    <header className="lid-hero">
      <div><span>{label.toUpperCase()} · PERSONAL TEAM ADVISOR</span><h1>LeagueIntel Desk</h1><p>Ask about your roster, trade fits, weekly decisions, playoff path and published league rules.</p></div>
      <b>READ ONLY · FAIR-PLAY SCOPE</b>
    </header>

    <section className="lid-boundary">
      <div><strong>What it knows</strong><span>Your team, public rosters, standings, rankings, projections, transactions, indexed reports and published bylaws.</span></div>
      <div><strong>What stays private</strong><span>Commissioner evidence, private notes, conversations, behavior dossiers, negotiation models and REGIME OS.</span></div>
    </section>

    <section className="lid-card">
      <div className="lid-card-head"><div><small>ASK AS {currentUser.display_name || 'LEAGUE MANAGER'}</small><h2>What do you want to understand?</h2></div><span>{label}</span></div>
      <LeagueDeskBot state={botState} />
      <div className="lid-prompts">{prompts[league].map(prompt => <button type="button" key={prompt} onClick={() => setQuestion(prompt)}>{prompt}</button>)}</div>
      <form onSubmit={ask}>
        <label htmlFor="league-desk-question">Your question</label>
        <textarea id="league-desk-question" value={question} onChange={event => setQuestion(event.target.value)} maxLength="2400" placeholder="Example: Which position should I upgrade, and which public roster is the cleanest fit?" />
        <button className="btn btn-primary" type="submit" disabled={busy || !question.trim()}>{busy ? 'Reading the league…' : 'Ask LeagueIntel'}</button>
      </form>
      {error && <div className="lid-error" role="alert">{error}</div>}
    </section>

    {result && <section className="lid-card lid-result" aria-live="polite">
      <header><div><small>LEAGUEINTEL ANSWER</small><h2>{result.headline}</h2></div><b className={`lid-confidence lid-confidence--${String(result.confidence).toLowerCase()}`}>{result.confidence} confidence</b></header>
      <p className="lid-answer">{result.answer}</p>
      {result.recommendation && <div className="lid-call"><small>RECOMMENDATION</small><strong>{result.recommendation}</strong></div>}
      <div className="lid-columns">
        {!!result.facts?.length && <article><h3>Verified facts</h3>{result.facts.map((item, index) => <p key={`fact-${index}`}>{item}</p>)}</article>}
        {!!result.inferences?.length && <article><h3>Analyst inference</h3>{result.inferences.map((item, index) => <p key={`inference-${index}`}>{item}</p>)}</article>}
      </div>
      {!!result.evidence?.length && <div className="lid-evidence"><h3>Evidence trail</h3>{result.evidence.map((item, index) => { const url = safeUrl(item.url); return <article key={`${item.label}-${index}`}><div><strong>{item.label}</strong><span>{item.source} · {displayTime(item.as_of)}</span></div><p>{item.detail}</p>{url && <a href={url} target="_blank" rel="noreferrer">Source ↗</a>}</article>; })}</div>}
      {!!result.caveats?.length && <div className="lid-caveats"><strong>LIMITS</strong><span>{result.caveats.join(' · ')}</span></div>}
      <footer><span>{result.action_policy}</span><time>{displayTime(result.generated_at)}</time></footer>
    </section>}
  </div>;
}
